import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Statistic,
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { userConsentService, UserConsent } from '../services/userConsentService';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const UserConsents: React.FC = () => {
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [consentTypeFilter, setConsentTypeFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  useEffect(() => {
    loadStatistics();
  }, [consentTypeFilter, dateRange]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (consentTypeFilter) {
        filters.consentType = consentTypeFilter;
      }
      if (dateRange && dateRange[0] && dateRange[1]) {
        filters.startDate = dateRange[0].format('YYYY-MM-DD');
        filters.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      const response = await userConsentService.getConsentStatistics(filters);
      setStatistics(response.data);
    } catch (error: any) {
      console.error('Failed to load consent statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getConsentTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      terms_of_service: 'blue',
      privacy_policy: 'green',
      data_processing: 'orange',
      marketing: 'purple',
      location: 'cyan',
      cookies: 'magenta',
    };
    return colors[type] || 'default';
  };

  const formatConsentType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>User Consent Logs</Title>
          </Col>
          <Col>
            <Space>
              <Select
                placeholder="Filter by type"
                allowClear
                style={{ width: 200 }}
                value={consentTypeFilter}
                onChange={setConsentTypeFilter}
              >
                <Option value="terms_of_service">Terms of Service</Option>
                <Option value="privacy_policy">Privacy Policy</Option>
                <Option value="data_processing">Data Processing</Option>
                <Option value="marketing">Marketing</Option>
                <Option value="location">Location</Option>
                <Option value="cookies">Cookies</Option>
              </Select>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              />
              <Button icon={<ReloadOutlined />} onClick={loadStatistics}>
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>

        {statistics && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Consents"
                    value={statistics.totalConsents}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Revoked"
                    value={statistics.totalRevoked}
                    prefix={<CloseCircleOutlined />}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Consent Rate"
                    value={statistics.totalConsents + statistics.totalRevoked > 0 
                      ? ((statistics.totalConsents / (statistics.totalConsents + statistics.totalRevoked)) * 100).toFixed(1)
                      : 0}
                    suffix="%"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
            </Row>

            <Card title="Consents by Type">
              <Table
                dataSource={statistics.byType || []}
                rowKey="_id"
                loading={loading}
                pagination={false}
                columns={[
                  {
                    title: 'Consent Type',
                    dataIndex: '_id',
                    key: 'type',
                    render: (type: string) => (
                      <Tag color={getConsentTypeColor(type)}>
                        {formatConsentType(type)}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Total',
                    dataIndex: 'total',
                    key: 'total',
                  },
                  {
                    title: 'Consented',
                    dataIndex: 'consented',
                    key: 'consented',
                    render: (value: number) => (
                      <Tag color="green">{value}</Tag>
                    ),
                  },
                  {
                    title: 'Revoked',
                    dataIndex: 'revoked',
                    key: 'revoked',
                    render: (value: number) => (
                      <Tag color="red">{value}</Tag>
                    ),
                  },
                  {
                    title: 'Consent Rate',
                    key: 'rate',
                    render: (_: any, record: any) => {
                      const rate = record.total > 0 
                        ? ((record.consented / record.total) * 100).toFixed(1)
                        : 0;
                      return `${rate}%`;
                    },
                  },
                ]}
              />
            </Card>
          </>
        )}
      </Card>
    </div>
  );
};

export default UserConsents;

